import { useRef, useState } from 'react';
import { Form, Input } from 'antd';
import type { FormInstance } from 'antd';
import type { TextAreaRef } from 'antd/es/input/TextArea';
import {
  COACH_EMAIL_TEXT_MAX,
  COACH_EMAIL_VARIABLES,
  DEFAULT_COACH_EMAIL_INTRO,
  effectiveCoachEmailIntro,
  renderCoachEmailText,
  type CoachEmailValues,
} from '@salaries/shared';

// The coach email's two box-editable texts — the opening paragraph and the
// signature — plus variable chips and a live preview. The part between them
// (totals, approve button, deadline) is fixed server-side and only shown here,
// greyed, so the operator sees where their text lands.
//
// Variable typos are rejected by BoxSettingsSchema on save (toast), not by a
// field rule — a field rule fails silently while this card is collapsed.

type Field = 'coach_email_intro' | 'coach_email_signature';

// Sample values for the preview only — the real ones are filled in per coach.
const PREVIEW_VALUES: CoachEmailValues = {
  שם_המאמן: 'דנה',
  שם_מלא: 'דנה כהן',
  חודש: 'ספטמבר 2026',
  שם_העסק: 'שם העסק',
  שם_הסניף: 'שם הסניף',
};

export function CoachEmailTextFields({ form }: { form: FormInstance }) {
  const intro = Form.useWatch('coach_email_intro', form) as string | null | undefined;
  const signature = Form.useWatch('coach_email_signature', form) as string | null | undefined;
  const refs = { coach_email_intro: useRef<TextAreaRef>(null), coach_email_signature: useRef<TextAreaRef>(null) };
  // Where a clicked variable goes: the last text box the operator was in.
  const [target, setTarget] = useState<Field>('coach_email_intro');

  const insert = (key: string) => {
    const el = refs[target].current?.resizableTextArea?.textArea;
    const current = (form.getFieldValue(target) as string | null) ?? '';
    const token = `{${key}}`;
    const start = el?.selectionStart ?? current.length;
    const end = el?.selectionEnd ?? current.length;
    form.setFieldValue(target, current.slice(0, start) + token + current.slice(end));
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(start + token.length, start + token.length);
    });
  };

  const previewIntro = renderCoachEmailText(effectiveCoachEmailIntro(intro), PREVIEW_VALUES);
  const previewSignature = signature?.trim() ? renderCoachEmailText(signature.trim(), PREVIEW_VALUES) : '';

  return (
    <>
      <div className="text-small text-secondary-text mb-3">
        ניתן להתאים את פתיחת המייל שנשלח לאנשי הצוות ולהוסיף חתימה, למשל שם החברה, ח״פ ופרטי קשר. הסכום
        לתשלום, הכפתור לאישור הדוח והמועד האחרון לאישור מופיעים תמיד ואינם ניתנים לעריכה.
      </div>

      <Form.Item name="coach_email_intro" label="פתיח" className="mb-3">
        <Input.TextArea
          ref={refs.coach_email_intro}
          onFocus={() => setTarget('coach_email_intro')}
          autoSize={{ minRows: 2, maxRows: 6 }}
          maxLength={COACH_EMAIL_TEXT_MAX}
          placeholder={DEFAULT_COACH_EMAIL_INTRO}
        />
      </Form.Item>

      <Form.Item name="coach_email_signature" label="חתימה" className="mb-3">
        <Input.TextArea
          ref={refs.coach_email_signature}
          onFocus={() => setTarget('coach_email_signature')}
          autoSize={{ minRows: 3, maxRows: 8 }}
          maxLength={COACH_EMAIL_TEXT_MAX}
          placeholder={'צוות {שם_העסק}\nשם החברה בע״מ · ח״פ 000000000\nטלפון: 000-0000000'}
        />
      </Form.Item>

      <div className="text-small text-secondary-text mb-1">
        לחיצה על משתנה מוסיפה אותו לשדה האחרון שנבחר. המשתנה יוחלף בערך המתאים לכל איש צוות:
      </div>
      <div className="flex flex-wrap gap-2 mb-4">
        {COACH_EMAIL_VARIABLES.map((v) => (
          <button
            key={v.key}
            type="button"
            title={v.label}
            onMouseDown={(e) => e.preventDefault() /* keep the text box's cursor */}
            onClick={() => insert(v.key)}
            className="px-2 py-0.5 rounded border text-small text-main-text bg-grey-bg hover:border-primary"
          >
            {`{${v.key}}`}
          </button>
        ))}
      </div>

      <div className="text-label font-medium text-main-text mb-1">תצוגה מקדימה</div>
      <div className="rounded border p-3 text-small text-main-text leading-relaxed" style={{ whiteSpace: 'pre-wrap' }}>
        <div>שלום {PREVIEW_VALUES.שם_המאמן},</div>
        <div className="mt-2">{previewIntro}</div>
        <div className="my-2 p-2 rounded bg-grey-bg text-secondary-text">
          סך הכל לתשלום: ₪4,200 (12 ימי עבודה).{'\n'}מצורפים קבצי הפירוט (Excel ו-PDF) לעיון.{'\n'}
          [ לאישור או שליחת הערות ]
        </div>
        {previewSignature && <div className="mt-2">{previewSignature}</div>}
      </div>
    </>
  );
}
